import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Add, Business, ChevronRight, Search } from '@material-ui/icons';
import { RouteComponentProps, withRouter } from 'react-router';
import { unpackToDateTime } from '@campfire/hot-date';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ENTERPRISE_SUB_ACCOUNTS } from './enterprise-sub-accounts.gql';
import { CreateSubAccountDialog } from './CreateSubAccountDialog';
import { AddEntitiesDialog } from './AddEntitiesDialog';
import { formatHeadlineEntityCounts } from './enterprise-entity-labels';
import { GetEnterpriseSubAccounts } from './__generated__/GetEnterpriseSubAccounts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    subName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    subDomain: {
      color: theme.color.grey.neutral200,
      fontSize: '12px',
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    mutedCellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
    },
    pendingBadge: {
      display: 'inline-block',
      minWidth: 22,
      textAlign: 'center' as const,
      borderRadius: 11,
      padding: '2px 8px',
      fontSize: '12px',
      fontWeight: 600,
    },
    pendingNone: {
      backgroundColor: '#efefef',
      color: theme.color.grey.neutral200,
    },
    searchField: {
      width: 200,
      '& .MuiOutlinedInput-root': {
        borderRadius: 4,
        fontSize: '13px',
        backgroundColor: 'white',
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 7,
        paddingBottom: 7,
      },
    },
    searchIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
    },
    chevron: {
      fontSize: '20px',
      color: theme.color.grey.neutral200,
    },
    rowButton: {
      textTransform: 'none' as const,
      fontSize: '13px',
      whiteSpace: 'nowrap' as const,
    },
    pendingSome: {
      backgroundColor: '#fff0e0',
      color: '#c77700',
    },
  })
);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'Never';
  const dt = unpackToDateTime(dateStr);
  if (!dt.isValid) return 'Never';
  return dt.toFormat('d MMM y');
};

const AdminConsoleSubAccountsComponent = (props: RouteComponentProps) => {
  const { history, match } = props;
  const classes = useStyles();
  const { isSm } = useCampfireTheme();

  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [addEntitiesTo, setAddEntitiesTo] = React.useState<{ tenantId: string; name: string } | undefined>();

  const { data, loading, refetch } = useCampfireQuery<GetEnterpriseSubAccounts, {}>(GET_ENTERPRISE_SUB_ACCOUNTS);

  const allSubAccounts = data?.tm.enterpriseSubAccounts ?? [];

  const searchTerm = search.trim().toLowerCase();
  const subAccounts =
    searchTerm === ''
      ? allSubAccounts
      : allSubAccounts.filter((subAccount) => subAccount.name.toLowerCase().includes(searchTerm));
  const withPending = subAccounts.filter((subAccount) => subAccount.pendingUpdateCount > 0).length;

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Sub-Accounts
          </Typography>
          <Typography variant='body2'>{'Manage the accounts that sit beneath this one'}</Typography>
        </Box>
        <ConsoleActionButton
          endIcon={<Add />}
          variant='contained'
          color='primary'
          onClick={() => setShowCreateDialog(true)}
        >
          Create Sub-Account
        </ConsoleActionButton>
      </Box>

      <CreateSubAccountDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={() => {
          if (refetch) {
            setTimeout(refetch, 0);
          }
        }}
      />

      {addEntitiesTo && (
        <AddEntitiesDialog
          open
          subTenantId={addEntitiesTo.tenantId}
          subAccountName={addEntitiesTo.name}
          onClose={() => setAddEntitiesTo(undefined)}
          onAdded={() => {
            if (refetch) {
              setTimeout(refetch, 0);
            }
          }}
        />
      )}

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && allSubAccounts.length === 0 ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <Business style={{ fontSize: '14px', paddingRight: '5px' }} /> Create your first sub-account
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {
              "You haven't created any sub-accounts yet. Click the 'Create Sub-Account' button top right to add your first."
            }
          </Typography>
        </Box>
      ) : (
        !loading && (
          <Box className={classes.containerPanel}>
            <Box
              display='flex'
              flexDirection={isSm ? 'column' : 'row'}
              alignItems={isSm ? 'left' : 'center'}
              justifyContent='space-between'
              mb='1rem'
            >
              <Box>
                <Typography className={classes.headingPanel}>
                  <Business style={{ fontSize: '14px', paddingRight: '5px' }} /> Sub-Accounts
                </Typography>
                <Typography variant='body2' className={classes.bodyPanel}>
                  {`You have ${allSubAccounts.length} sub-account${allSubAccounts.length !== 1 ? 's' : ''}${
                    withPending > 0 ? `, ${withPending} with pending updates` : ''
                  }.`}
                </Typography>
              </Box>
              <TextField
                variant='outlined'
                size='small'
                placeholder='Search sub-accounts'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell>Sub-Account</TableCell>
                    <TableCell>Admins</TableCell>
                    <TableCell>Entities</TableCell>
                    <TableCell>Last Pushed</TableCell>
                    <TableCell align='right'>Pending</TableCell>
                    <TableCell align='right'>{''}</TableCell>
                    <TableCell padding='checkbox'>{''}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography className={classes.bodyPanel}>{`No sub-accounts match '${search}'.`}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {subAccounts.map((subAccount) => (
                    <TableRow
                      key={subAccount.tenantId}
                      hover
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push(`${match.url}/${subAccount.tenantId}`)}
                    >
                      <TableCell>
                        <Typography className={classes.subName}>{subAccount.name}</Typography>
                        <Typography className={classes.subDomain}>{subAccount.fqdn}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.cellText}>
                          {subAccount.admins.length === 0
                            ? 'No admins yet'
                            : `${subAccount.admins.length} admin${subAccount.admins.length !== 1 ? 's' : ''}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.cellText}>
                          {formatHeadlineEntityCounts(subAccount.mappedEntityCounts)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.mutedCellText}>{formatDate(subAccount.lastPushedAt)}</Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <span
                          className={`${classes.pendingBadge} ${
                            subAccount.pendingUpdateCount > 0 ? classes.pendingSome : classes.pendingNone
                          }`}
                        >
                          {subAccount.pendingUpdateCount}
                        </span>
                      </TableCell>
                      <TableCell align='right'>
                        <Button
                          size='small'
                          variant='outlined'
                          color='primary'
                          disableElevation
                          className={classes.rowButton}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setAddEntitiesTo({ tenantId: subAccount.tenantId, name: subAccount.name });
                          }}
                        >
                          Add Entities
                        </Button>
                      </TableCell>
                      <TableCell padding='checkbox'>
                        <ChevronRight className={classes.chevron} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
    </Box>
  );
};

export const AdminConsoleSubAccounts = withRouter(AdminConsoleSubAccountsComponent);
